import { useMemo } from 'react';
import type { ItemInActiveReturn } from '@babackapp/private-api-client/client/use-get-items-in-active-returns';
import useTranslation from '@babackapp/translations/use-translation';

interface UseConflictingItemsParams {
  lineItemId: string;
  itemsInActiveReturns: ItemInActiveReturn[] | undefined;
  currentQuantity: number;
  maxQuantity: number;
}

interface ConflictingItemsResult {
  conflictingItems: ItemInActiveReturn[];
  totalConflictingQuantity: number;
  adjustedMaxQuantity: number;
  isDisabled: boolean;
  warningMessage: string | null;
  tooltipContent: string | null;
}

export function useConflictingItems({
  lineItemId,
  itemsInActiveReturns,
  currentQuantity,
  maxQuantity,
}: UseConflictingItemsParams): ConflictingItemsResult {
  const t = useTranslation();

  return useMemo(() => {
    if (!itemsInActiveReturns?.length) {
      return {
        conflictingItems: [],
        totalConflictingQuantity: 0,
        adjustedMaxQuantity: maxQuantity,
        isDisabled: false,
        warningMessage: null,
        tooltipContent: null,
      };
    }

    // Find all conflicts for this lineItemId
    const conflictingItems = itemsInActiveReturns.filter(
      (item) => item.lineItemId === lineItemId
    );

    if (!conflictingItems.length) {
      return {
        conflictingItems: [],
        totalConflictingQuantity: 0,
        adjustedMaxQuantity: maxQuantity,
        isDisabled: false,
        warningMessage: null,
        tooltipContent: null,
      };
    }

    // Aggregate quantities across all returns
    const totalConflictingQuantity = conflictingItems.reduce(
      (sum, item) => sum + item.quantity,
      0
    );

    // Calculate adjusted max quantity
    const adjustedMaxQuantity = Math.max(0, maxQuantity - totalConflictingQuantity);

    // Determine if this item should be disabled
    const isDisabled = currentQuantity > adjustedMaxQuantity;

    // Get unique returns
    const uniqueReturnsCount = new Set(
      conflictingItems.map(item => item.returnRequestId)
    ).size;

    // Build concise warning message with translations
    const returnsLabel = uniqueReturnsCount === 1
      ? t('returns.conflicts.one_return')
      : t('returns.conflicts.other_returns');

    const warningMessage = t('returns.conflicts.in_other_returns', {
      count: totalConflictingQuantity,
      returns: returnsLabel,
    });

    // Tooltip content is built in the component
    const tooltipContent = null;

    return {
      conflictingItems,
      totalConflictingQuantity,
      adjustedMaxQuantity,
      isDisabled,
      warningMessage,
      tooltipContent,
    };
  }, [lineItemId, itemsInActiveReturns, currentQuantity, maxQuantity, t]);
}
